import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader, CardFooter } from '../components/ui/Card';
import { StatChip } from '../components/ui/StatChip';
import { CtaSection } from '../components/sections/CtaSection';
import { BookingEmbed } from '../components/sections/BookingEmbed';
import { CheckIcon, AwardIcon, LightbulbIcon, BriefcaseIcon, UsersIcon, GraduationCapIcon, StarIcon, TrendingUpIcon, BarChartIcon, BuildingIcon, CalendarIcon, ClipboardCheckIcon, DollarSignIcon, HeartIcon, UserIcon, HandshakeIcon, TargetIcon, BookOpenIcon, PresentationIcon, BedIcon, UtensilsIcon, MapPinIcon, MusicIcon, GlassesIcon, ShoppingBagIcon, PartyPopperIcon } from 'lucide-react';
export function ElevateImpact() {
  const [activePersona, setActivePersona] = useState('sales');
  const [activeAgendaDay, setActiveAgendaDay] = useState(0);
  const personas = [{
    id: 'sales',
    title: 'Revenue Leaders',
    roles: 'CRO / VP Sales / Sales Enablement',
    icon: <TargetIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1144" />,
    hook: 'Prototype a high-impact SKO in 48 hours—leave with a ready-to-run playbook.',
    value: 'Design an unforgettable SKO/offsite and lift win-rates & quota attainment.'
  }, {
    id: 'executives',
    title: 'C-Suite Support',
    roles: 'Chief of Staff / EA to CEO',
    icon: <BuildingIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1145" />,
    hook: 'Pre-packaged, compliance-ready board/C-suite retreat you can approve in one memo.',
    value: 'Streamline logistics and calendar for board/C-suite offsites with quick approval.'
  }, {
    id: 'strategy',
    title: 'Strategy Leaders',
    roles: 'Strategy / Transformation / PMO',
    icon: <PresentationIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1146" />,
    hook: 'Run a 6-hour strategy sprint with elite faculty + turn into a quarterly cadence.',
    value: 'Create focused strategy sprints away from HQ with facilitated outcomes.'
  }, {
    id: 'partners',
    title: 'Channel & Partners',
    roles: 'Channel / Alliances / Partner Marketing',
    icon: <HandshakeIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1147" />,
    hook: 'Curate a partner summit that upgrades MDF outcomes and pipeline influence.',
    value: 'Host impactful partner councils and tier-1 partner summits.'
  }, {
    id: 'customers',
    title: 'Customer Advocates',
    roles: 'Customer Marketing / Community',
    icon: <UsersIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1148" />,
    hook: 'Blueprint a Customer Advisory Board experience that increases expansion NRR.',
    value: "Create elite customer councils and user group leaders' retreats."
  }, {
    id: 'investors',
    title: 'Investment Leaders',
    roles: 'PE/VC Platform & Portfolio Ops',
    icon: <DollarSignIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1149" />,
    hook: 'Turn your annual portco meetup into a growth lab (templates + faculty included).',
    value: 'Organize portfolio CEO summits and operator bootcamps with measurable outcomes.'
  }, {
    id: 'education',
    title: 'Education Directors',
    roles: 'University Exec-Ed Program Directors',
    icon: <BookOpenIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1150" />,
    hook: 'Co-deliver a custom exec-ed in an immersive setting (CPD-friendly agenda).',
    value: 'Find distinctive venues for custom programs with complete experience packages.'
  }, {
    id: 'hr',
    title: 'People & Culture',
    roles: 'HR Benefits / Wellbeing / DEI',
    icon: <HeartIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1151" />,
    hook: 'Wellbeing x Culture immersion with measurable eNPS lift and CPD-submittable agenda.',
    value: 'Address burnout recovery and culture & belonging initiatives.'
  }, {
    id: 'marketing',
    title: 'Brand Experience',
    roles: 'Corporate Marketing / Field Marketing',
    icon: <StarIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1152" />,
    hook: 'Host a lighthouse client experience that produces case-study content in 72 hours.',
    value: 'Create flagship clienteling or product-launch councils with measurable outcomes.'
  }];
  const valuePropositions = {
    sales: {
      title: 'Revenue Leaders',
      subtitle: 'Sales, Alliances, Customer & Field Marketing',
      points: ['Ready-to-run SKO/partner/CAB agenda, demonstrated live', 'Measurable pipeline/NRR impact model with metrics dashboard', 'Co-created content templates you can immediately implement', 'Peer networking with other revenue leaders across industries'],
      image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    },
    executives: {
      title: 'C-Suite Support',
      subtitle: 'Chiefs of Staff, Executive Assistants, Board Coordinators',
      points: ["'One-memo approval' package with complete agenda, budget, and ROI sheet", 'Risk/compliance documentation ready for legal review', 'Turnkey logistics that reduce your planning overhead by 80%', 'Executive-ready presentations to secure immediate buy-in'],
      image: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    },
    strategy: {
      title: 'Strategy Leaders',
      subtitle: 'Strategy, Transformation & PMO Directors',
      points: ['Facilitated strategy sprint framework with proven outcomes', 'Quarterly cadence template to maintain momentum post-event', 'Cross-functional alignment tools designed by top consultants', 'Implementation roadmap with accountable next steps'],
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    },
    partners: {
      title: 'Channel & Partners',
      subtitle: 'Alliances, Channel & Partner Marketing Leaders',
      points: ['Partner summit blueprint that maximizes MDF utilization', 'Pipeline influence measurement framework', 'Tier-based partner experience design with scalable components', 'Co-marketing and enablement templates ready for implementation'],
      image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80'
    },
    customers: {
      title: 'Customer Advocates',
      subtitle: 'Customer Marketing, Community & Developer Relations',
      points: ['Customer Advisory Board framework that drives expansion revenue', 'Community leader engagement model with retention metrics', 'Voice of Customer capture methodology with product feedback loop', 'Case study and testimonial generation process built-in'],
      image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    },
    investors: {
      title: 'Investment Leaders',
      subtitle: 'PE/VC Platform & Portfolio Operations',
      points: ['Portfolio CEO summit design with value-creation workshops', 'Cross-portfolio playbook exchange framework', 'Filmed case content for LP communications', 'Operator bootcamp template with measurable acceleration metrics'],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80'
    },
    education: {
      title: 'Education Directors',
      subtitle: 'University Exec-Ed & Custom Program Directors',
      points: ['White-label executive education environment in an iconic setting', 'CPD-compliant agenda structure ready for accreditation', 'Sponsor-ready program components with monetization options', 'Faculty support and content integration with your curriculum'],
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    },
    hr: {
      title: 'People & Culture',
      subtitle: 'HR, Wellbeing & DEI Leaders',
      points: ['CPD-submittable learning hours for professional development', 'Measurable wellbeing outcomes with eNPS impact tracking', 'Culture rituals designed for continuation back at the workplace', 'Belonging and inclusion frameworks for diverse teams'],
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    },
    marketing: {
      title: 'Brand Experience',
      subtitle: 'Corporate Marketing, Brand & Field Marketing',
      points: ['Lighthouse client experience blueprint with measurable outcomes', 'Case study content production integrated into the experience', 'Product launch council framework with influencer components', 'Experiential marketing measurement with attribution modeling'],
      image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
    }
  };
  const agendaItems = [{
    day: 'Day 1',
    timeframe: 'Afternoon & Evening',
    items: [{
      time: '2:00 PM',
      title: 'VIP Arrivals & Welcome',
      description: 'Private check-in experience with personalized welcome'
    }, {
      time: '3:30 PM',
      title: 'Destination ROI Masterclass',
      description: '90-minute session on maximizing event impact and measuring outcomes'
    }, {
      time: '6:00 PM',
      title: 'Immersive Welcome Reception',
      description: 'Networking and showcase of destination experiences'
    }, {
      time: '7:30 PM',
      title: 'Curated Dining Experience',
      description: 'Signature culinary journey with facilitated connections'
    }, {
      time: '9:00 PM',
      title: 'Live Concert Experience',
      description: "Enjoy Hard Rock's signature live music entertainment"
    }]
  }, {
    day: 'Day 2',
    timeframe: 'Full Day Experience',
    items: [{
      time: '7:00 AM',
      title: 'Optional Wellness Activity',
      description: 'Sunrise yoga or guided beach meditation'
    }, {
      time: '8:30 AM',
      title: 'Breakfast & Track Selection',
      description: 'Choose your focused learning track'
    }, {
      time: '9:30 AM',
      title: 'Specialized Tracks',
      description: 'Revenue SKO / People & Culture / Partner Council (3 hours)'
    }, {
      time: '12:30 PM',
      title: 'Collaborative Lunch',
      description: 'Shared insights and cross-track pollination'
    }, {
      time: '2:00 PM',
      title: 'Site Tours & Experiential Showcase',
      description: 'Curated exploration of venue capabilities'
    }, {
      time: '4:00 PM',
      title: 'Design Lab',
      description: 'Facilitated workshop to apply learnings to your specific needs'
    }, {
      time: '7:00 PM',
      title: 'Signature Networking Experience',
      description: 'Immersive evening with local cultural elements'
    }, {
      time: '9:30 PM',
      title: 'Themed Party Night',
      description: "Hard Rock's famous themed entertainment event"
    }]
  }, {
    day: 'Day 3',
    timeframe: 'Morning',
    items: [{
      time: '8:00 AM',
      title: 'Breakfast & Reflection',
      description: 'Guided discussion on key takeaways'
    }, {
      time: '9:00 AM',
      title: 'Playbook Finalization',
      description: 'Complete your actionable framework with expert guidance'
    }, {
      time: '10:30 AM',
      title: 'One-to-One Consultations',
      description: 'Personalized next steps with our specialists'
    }, {
      time: '12:00 PM',
      title: 'Farewell Lunch & Departures',
      description: 'Closing remarks and continuation planning'
    }]
  }];
  const hardRockActivities = [{
    title: 'Live Music & Concerts',
    icon: <MusicIcon className="h-8 w-8 text-white" data-id="element-1153" />,
    description: 'Enjoy nightly concerts featuring top tribute bands and local talent on multiple stages throughout the resort.'
  }, {
    title: 'Legendary Pool Parties',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-1154" />,
    description: 'Experience the famous Hard Rock pool parties with DJs, foam parties, and poolside entertainment.'
  }, {
    title: 'Themed Nights & Shows',
    icon: <PartyPopperIcon className="h-8 w-8 text-white" data-id="element-1155" />,
    description: 'Participate in themed dinner shows, Mexican fiestas, and professional entertainment productions.'
  }, {
    title: 'Rock Spa® Treatments',
    icon: <HeartIcon className="h-8 w-8 text-white" data-id="element-1156" />,
    description: 'Unwind with signature rhythm and motion treatments, massages, and wellness therapies.'
  }, {
    title: 'Music Amenities Program',
    icon: <GlassesIcon className="h-8 w-8 text-white" data-id="element-1157" />,
    description: 'Borrow a Fender® guitar, mixing board, or vinyl record player delivered to your room.'
  }, {
    title: 'VIP Access Benefits',
    icon: <ShoppingBagIcon className="h-8 w-8 text-white" data-id="element-1158" />,
    description: 'Enjoy exclusive access to premium amenities, priority reservations, and personalized concierge service.'
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1159">
      {/* Hero */}
      <section className="relative py-32 md:py-40 overflow-hidden" data-id="element-1160">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-1161">
          <img src="https://images.unsplash.com/photo-1682553064441-b3bcb7a58d0c?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.0.3" alt="Cancun Beach Paradise" className="w-full h-full object-cover" data-id="element-1162" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0099A1]/90 to-[#374047]/70" data-id="element-1163"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1164">
          <div className="max-w-3xl" data-id="element-1165">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-md" data-id="element-1166">
              Your Complimentary Luxury Retreat
            </h1>
            <p className="text-xl text-white/90 mb-8 drop-shadow-md" data-id="element-1167">
              Escape to paradise for a FREE two-night strategic immersion where
              elite decision-makers design unforgettable corporate experiences
              that drive measurable results
            </p>
            <div className="mt-8" data-id="element-1168">
              <Button variant="primary" size="lg" to="/contact" data-id="element-1169">
                Apply for Invitation
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Persona Selector - Redesigned for better mobile experience */}
      <section className="py-12 bg-white" data-id="element-1170">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1171">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-1172">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1173">
              Tailored for Decision-Makers Like You
            </h2>
            <p className="text-lg text-[#374047]/80 mb-4" data-id="element-1174">
              Select your role below to see content specific to your objectives
            </p>
            {/* Visual indicator to show this is interactive */}
            <div className="inline-flex items-center bg-[#0099A1]/10 px-4 py-2 rounded-lg text-[#0099A1] font-medium mb-6" data-id="element-1175">
              <CalendarIcon className="h-5 w-5 mr-2" data-id="element-1176" />
              <span data-id="element-1177">Tap a role to view tailored content</span>
            </div>
          </div>
          {/* Mobile-optimized role selector */}
          <div className="overflow-x-auto pb-4 mb-6" data-id="element-1178">
            <div className="flex space-x-2 min-w-max px-1" data-id="element-1179">
              {personas.map(persona => <button key={persona.id} onClick={() => setActivePersona(persona.id)} className={`flex flex-col items-center px-3 py-3 rounded-lg text-sm font-medium transition-colors ${activePersona === persona.id ? 'bg-[#0099A1] text-white' : 'bg-[#F4E8D0]/50 text-[#374047] hover:bg-[#0099A1]/10'}`} data-id="element-1180">
                  <span className="p-2 bg-white/80 rounded-full mb-2" data-id="element-1181">
                    {persona.icon}
                  </span>
                  <span className="text-center whitespace-nowrap" data-id="element-1182">
                    {persona.title}
                  </span>
                  {activePersona === persona.id && <div className="h-1 w-12 bg-[#FF6B65] rounded-full mt-2" data-id="element-1183"></div>}
                </button>)}
            </div>
          </div>
          {/* Active persona indicator */}
          <div className="bg-[#F4E8D0]/30 rounded-lg p-4 mb-6" data-id="element-1184">
            <div className="flex items-center justify-between" data-id="element-1185">
              <div className="flex items-center" data-id="element-1186">
                <div className="bg-[#0099A1] p-2 rounded-full text-white mr-3" data-id="element-1187">
                  {personas.find(p => p.id === activePersona)?.icon}
                </div>
                <div data-id="element-1188">
                  <h3 className="font-bold text-[#374047]" data-id="element-1189">
                    {valuePropositions[activePersona].title}
                  </h3>
                  <p className="text-sm text-[#374047]/70" data-id="element-1190">
                    {personas.find(p => p.id === activePersona)?.roles}
                  </p>
                </div>
              </div>
              <div className="bg-[#0099A1]/10 px-3 py-1 rounded-full text-xs text-[#0099A1] font-medium" data-id="element-1191">
                Selected
              </div>
            </div>
          </div>
          {/* Active Persona Value Proposition - Redesigned without image */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 mb-8" data-id="element-1192">
            <div className="border-l-4 border-[#FF6B65] pl-4 mb-6" data-id="element-1193">
              <p className="text-lg italic text-[#374047]" data-id="element-1194">
                "{personas.find(p => p.id === activePersona)?.hook}"
              </p>
            </div>
            <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1195">
              {personas.find(p => p.id === activePersona)?.value}
            </p>
            <h4 className="font-bold text-[#374047] mb-4" data-id="element-1196">Key Benefits:</h4>
            <div className="space-y-4 mb-8" data-id="element-1197">
              {valuePropositions[activePersona].points.map((point, index) => <div key={index} className="flex items-start bg-[#F4E8D0]/20 p-3 rounded-lg" data-id="element-1198">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1199" />
                  <span className="ml-3 text-[#374047]" data-id="element-1200">{point}</span>
                </div>)}
            </div>
            <div className="flex justify-center" data-id="element-1201">
              <Button variant="primary" to="/contact" data-id="element-1202">
                Request Your Invitation
              </Button>
            </div>
          </div>
          {/* Navigation hints */}
          <div className="text-center text-[#374047]/60 text-sm mb-8" data-id="element-1203">
            <p data-id="element-1204">
              Scroll through the roles above to find the best fit for your
              position
            </p>
          </div>
        </div>
      </section>
      {/* Package Details - Condensed version */}
      <section className="py-12 bg-[#F4E8D0]/30" data-id="element-1205">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1206">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-1207">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1208">
              Your Exclusive 2-Night Invitation
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1209">
              Limited to 20 qualified decision-makers per cohort
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1210">
            <div data-id="element-1211">
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-1212">
                <CheckIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1213" />
                What's Included
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-id="element-1214">
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1215">
                  <BedIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1216" />
                  <span className="text-sm" data-id="element-1217">
                    Two nights at a luxury property
                  </span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1218">
                  <UtensilsIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1219" />
                  <span className="text-sm" data-id="element-1220">Curated culinary experiences</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1221">
                  <MapPinIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1222" />
                  <span className="text-sm" data-id="element-1223">Local transfers and logistics</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1224">
                  <BookOpenIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1225" />
                  <span className="text-sm" data-id="element-1226">Workshops with elite faculty</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1227">
                  <ClipboardCheckIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1228" />
                  <span className="text-sm" data-id="element-1229">Complete work outputs</span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1230">
                  <HeartIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1231" />
                  <span className="text-sm" data-id="element-1232">Personal wellness elements</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-[#374047] mb-4 flex items-center" data-id="element-1233">
                <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1234" />
                What You Cover
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1235">
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1236">
                  <CalendarIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1237" />
                  <span className="text-sm" data-id="element-1238">
                    Your flights to the destination
                  </span>
                </div>
                <div className="bg-white/60 p-3 rounded-lg flex items-start" data-id="element-1239">
                  <DollarSignIcon className="h-4 w-4 text-[#0099A1] mt-0.5 mr-2" data-id="element-1240" />
                  <span className="text-sm" data-id="element-1241">$250 refundable deposit</span>
                </div>
              </div>
              <div className="mt-6" data-id="element-1242">
                <Button variant="primary" to="/contact" data-id="element-1243">
                  Check Eligibility
                </Button>
              </div>
            </div>
            <div data-id="element-1244">
              <Card className="h-full" data-id="element-1245">
                <CardHeader className="bg-[#0099A1] text-white p-4" data-id="element-1246">
                  <h3 className="text-xl font-bold" data-id="element-1247">
                    Compliance-Ready Package
                  </h3>
                </CardHeader>
                <CardBody className="p-6" data-id="element-1248">
                  <div className="grid grid-cols-2 gap-4" data-id="element-1249">
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1250">
                      <div className="flex items-center mb-2" data-id="element-1251">
                        <ClipboardCheckIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1252" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1253">
                          Official Letter
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1254">
                        Educational objectives & hospitality breakdown for
                        approval
                      </p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1255">
                      <div className="flex items-center mb-2" data-id="element-1256">
                        <BookOpenIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1257" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1258">
                          CPD Agenda
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1259">
                        Submit for professional development credit
                      </p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1260">
                      <div className="flex items-center mb-2" data-id="element-1261">
                        <DollarSignIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1262" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1263">
                          Value Disclosure
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1264">
                        Transparent line-item benefits for compliance
                      </p>
                    </div>
                    <div className="bg-white/60 rounded-lg p-3" data-id="element-1265">
                      <div className="flex items-center mb-2" data-id="element-1266">
                        <UserIcon className="h-5 w-5 text-[#0099A1] mr-2" data-id="element-1267" />
                        <h4 className="font-medium text-[#374047]" data-id="element-1268">
                          No Obligation
                        </h4>
                      </div>
                      <p className="text-xs text-[#374047]/70" data-id="element-1269">
                        Clear documentation of no-pressure policy
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Sample Agenda - Converted to tabs */}
      <section className="py-16 bg-white" data-id="element-1270">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1271">
          <div className="max-w-3xl mx-auto text-center mb-8" data-id="element-1272">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1273">
              Sample 48-Hour Agenda
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1274">
              A carefully crafted experience with modular tracks to address your
              specific needs
            </p>
          </div>
          {/* Day Tabs */}
          <div className="flex border-b border-gray-200 mb-6 overflow-x-auto" data-id="element-1275">
            {agendaItems.map((day, index) => <button key={index} onClick={() => setActiveAgendaDay(index)} className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${activeAgendaDay === index ? 'border-b-2 border-[#0099A1] text-[#0099A1]' : 'text-[#374047]/70 hover:text-[#374047]'}`} data-id="element-1276">
                <div className="flex items-center" data-id="element-1277">
                  <div className="w-6 h-6 rounded-full bg-[#0099A1]/10 text-[#0099A1] flex items-center justify-center mr-2 text-xs" data-id="element-1278">
                    {index + 1}
                  </div>
                  {day.day} - {day.timeframe}
                </div>
              </button>)}
          </div>
          {/* Active Day Content */}
          <div className="bg-[#F4E8D0]/20 rounded-xl p-6" data-id="element-1279">
            <div className="space-y-6" data-id="element-1280">
              {agendaItems[activeAgendaDay].items.map((item, itemIndex) => <div key={itemIndex} className="flex" data-id="element-1281">
                  <div className="w-24 flex-shrink-0 text-[#374047]/70 font-medium" data-id="element-1282">
                    {item.time}
                  </div>
                  <div data-id="element-1283">
                    <h4 className="font-medium text-[#374047]" data-id="element-1284">{item.title}</h4>
                    <p className="text-sm text-[#374047]/70" data-id="element-1285">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="max-w-3xl mx-auto text-center mt-8" data-id="element-1286">
            <p className="text-[#374047]/70" data-id="element-1287">
              This agenda is customized for each cohort based on the specific
              roles and objectives of participants
            </p>
          </div>
        </div>
      </section>
      {/* Luxury Accommodations */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1288">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1289">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1290">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1291">
              Your Luxury Accommodations
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1292">
              Enjoy a complimentary two-night stay at the iconic Hard Rock Hotel
              Cancun
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-1293">
            <div data-id="element-1294">
              <div className="flex items-center mb-6" data-id="element-1295">
                <BedIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-1296" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1297">
                  Luxurious Oceanfront Experience
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1298">
                Experience the ultimate comfort in meticulously designed rooms
                featuring ocean views, hydro spa tubs, and premium amenities.
                Your complimentary stay includes:
              </p>
              <div className="space-y-4 mb-8" data-id="element-1299">
                <div className="flex items-start" data-id="element-1300">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1301" />
                  <span className="ml-3 text-[#374047]" data-id="element-1302">
                    Spacious suites with breathtaking Caribbean views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1303">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1304" />
                  <span className="ml-3 text-[#374047]" data-id="element-1305">
                    In-room hydro spa tubs for ultimate relaxation
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1306">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1307" />
                  <span className="ml-3 text-[#374047]" data-id="element-1308">
                    Private balconies with panoramic views
                  </span>
                </div>
                <div className="flex items-start" data-id="element-1309">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1310" />
                  <span className="ml-3 text-[#374047]" data-id="element-1311">
                    Premium amenities and 24-hour room service
                  </span>
                </div>
              </div>
              <div className="flex items-center mb-6" data-id="element-1312">
                <UtensilsIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-1313" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1314">
                  World-Class Dining
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1315">
                Indulge in exceptional culinary experiences across multiple
                restaurants offering international cuisines prepared by skilled
                chefs, including a hosted dinner at the acclaimed Bombay-Cancun
                restaurant.
              </p>
              <div className="flex items-center mb-6" data-id="element-1316">
                <MapPinIcon className="h-6 w-6 text-[#0099A1] mr-3" data-id="element-1317" />
                <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1318">
                  Perfect Location
                </h3>
              </div>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-1319">
                Situated in the heart of Cancun's Hotel Zone, you'll enjoy
                pristine beaches, infinity pools, and easy access to local
                attractions while focusing on your strategic planning.
              </p>
              <Button variant="primary" to="/contact" data-id="element-1320">
                Apply for Your Stay
              </Button>
            </div>
            <div data-id="element-1321">
              <div className="grid grid-cols-2 gap-4" data-id="element-1322">
                <div className="overflow-hidden rounded-lg" data-id="element-1323">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/uqWCKaxJJQHPR63bhKsnNi/hard-rock-hotel-cancun-rock-suite-ocean-view-king-bed.jpg" alt="Hard Rock Hotel Cancun Suite" className="w-full h-48 object-cover" data-id="element-1324" />
                </div>
                <div className="overflow-hidden rounded-lg" data-id="element-1325">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/32xHwSugA785R6rs27jzyA/13830703_ImageLargeWidth.jpg" alt="Hard Rock Hotel Cancun Pool" className="w-full h-48 object-cover" data-id="element-1326" />
                </div>
                <div className="overflow-hidden rounded-lg" data-id="element-1327">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4zXYgDX4yC8uvXGZ5Lt7Q5/fine-dining-all-inclusive-hard-rock-cancun.jpg" alt="Hard Rock Hotel Cancun Dining" className="w-full h-48 object-cover" data-id="element-1328" />
                </div>
                <div className="overflow-hidden rounded-lg" data-id="element-1329">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vjnr4pnk2Td97NQSPj6RK5/HRH_Cancun_Pool_Beach_013_6465.jpg" alt="Hard Rock Hotel Cancun Beach" className="w-full h-48 object-cover" data-id="element-1330" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Hard Rock Activities */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-1331">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1332">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1333">
            <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-1334">
              Hard Rock Cancun Entertainment & Activities
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-1335">
              Work hard, play harder with these amazing experiences included in
              your stay
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1336">
            {hardRockActivities.map((activity, index) => <div key={index} className="bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-xl p-6 text-white shadow-lg transform transition-transform hover:scale-105" data-id="element-1337">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4" data-id="element-1338">
                  {activity.icon}
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-1339">{activity.title}</h3>
                <p className="text-white/80" data-id="element-1340">{activity.description}</p>
              </div>)}
          </div>
          <div className="max-w-3xl mx-auto text-center mt-12" data-id="element-1341">
            <p className="text-[#374047]/70 mb-6" data-id="element-1342">
              All activities and entertainment are included in your
              complimentary stay
            </p>
            <Button variant="secondary" to="/contact" data-id="element-1343">
              Apply for Your Rock Star Experience
            </Button>
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 relative overflow-hidden" style={{
      background: 'linear-gradient(to right, #0099A1, #006D73)'
    }} data-id="element-1344">
        <div className="absolute inset-0 opacity-20" data-id="element-1345">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vjnr4pnk2Td97NQSPj6RK5/HRH_Cancun_Pool_Beach_013_6465.jpg" alt="Hard Rock Hotel Cancun Background" className="w-full h-full object-cover" data-id="element-1346" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1347">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1348">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/d4ZJyP7bz15Ef5qLdnhr7b/HardRockHotelLogo-White3x.png" alt="Hard Rock Hotel Logo" className="h-16 mx-auto mb-6" data-id="element-1349" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1350">
              Limited Availability
            </h2>
            <p className="text-xl text-white/90 mb-8" data-id="element-1351">
              Only 20 qualified decision-makers per cohort. Secure your spot
              today.
            </p>
            <Button variant="primary" size="lg" to="/contact" data-id="element-1352">
              Apply for Your Invitation
            </Button>
            <p className="text-sm text-white/70 mt-4" data-id="element-1353">
              Our team will review your application within 48 hours
            </p>
          </div>
        </div>
      </section>
      {/* Google Booking Embed */}
      <BookingEmbed data-id="element-1354" />
      <CtaSection data-id="element-1355" />
    </div>;
}